import { type FC, useState } from 'react'
import { Form } from 'react-bootstrap'
import classNames from 'classnames'
import { Modal } from '../../../components/Modal/Modal'
import { type Location, type LocationKind } from '../../../models/Scheduling'
import { useAppSelector } from '../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { useGetSchedulingEventTypesQuery } from '../../../store/scheduling/scheduling'
import { countAffectedEventTypes } from '../flows/form/scheduleHelpers'
import { formatTimezoneLabel } from './schedulingDisplay'

export type LocationImpactVariant = 'update' | 'change-default' | 'delete'

interface LocationImpactDialogProps {
  variant: LocationImpactVariant
  current: Location
  next?: Location
  replacementOptions?: Location[]
  defaultKind?: LocationKind
  isLoading?: boolean
  onConfirm: (replacementId?: string) => void
  onCancel: () => void
}

const VARIANT_CONFIG: Record<LocationImpactVariant, { title: string; confirmLabel: string; danger: boolean }> = {
  update: { title: 'Updating location?', confirmLabel: 'Update location', danger: false },
  'change-default': { title: 'Changing default location?', confirmLabel: 'Change default', danger: false },
  delete: { title: 'Deleting location?', confirmLabel: 'Delete location', danger: true },
}

const pluralizeEventTypes = (count: number): string => `${count} shared event type${count === 1 ? '' : 's'}`

const LocationCard: FC<{ location: Location; struckAgainst?: Location }> = ({ location, struckAgainst }) => {
  const struck = (field: keyof Location): boolean => Boolean(struckAgainst) && struckAgainst?.[field] !== location[field]
  return (
    <div className='d-flex flex-column zocks-gap-0.25 flex-grow-1 p-3 rounded-3 zocks-bg-color-gray-100'>
      <span className='bold paragraph-medium'>{location.name}</span>
      <span
        className={classNames('d-flex align-items-center zocks-gap-0.5 paragraph-medium zocks-text-gray-600', {
          'text-decoration-line-through': struck('address'),
        })}
      >
        <i className='bi bi-geo-alt flex-shrink-0' />
        {location.address}
      </span>
      {location.note ? (
        <span
          className={classNames('d-flex align-items-center zocks-gap-0.5 paragraph-medium zocks-text-gray-600', {
            'text-decoration-line-through': struck('note'),
          })}
        >
          <i className='bi bi-card-text flex-shrink-0' />
          {location.note}
        </span>
      ) : null}
      {location.timeZone ? (
        <span
          className={classNames('d-flex align-items-center zocks-gap-0.5 paragraph-medium zocks-text-gray-600', {
            'text-decoration-line-through': struck('timeZone'),
          })}
        >
          <i className='bi bi-globe flex-shrink-0' />
          {formatTimezoneLabel(location.timeZone)}
        </span>
      ) : null}
    </div>
  )
}

export const LocationImpactDialog: FC<LocationImpactDialogProps> = ({
  variant,
  current,
  next,
  replacementOptions,
  defaultKind,
  isLoading,
  onConfirm,
  onCancel,
}) => {
  const accountId = useAppSelector(selectSafeAuthenticatedUser).accountId
  const { data: eventTypes, isSuccess } = useGetSchedulingEventTypesQuery({ accountId })
  const config = VARIANT_CONFIG[variant]

  const affectedCount = countAffectedEventTypes(eventTypes ?? [], current, variant === 'change-default', defaultKind)
  const isUnusedDelete = variant === 'delete' && isSuccess && affectedCount === 0
  const [replacementId, setReplacementId] = useState<string | undefined>(replacementOptions?.[0]?.id)
  const replacement = replacementOptions?.find((option) => option.id === replacementId)

  const replacementTimezoneChanges = replacement?.timeZone && current.timeZone ? replacement.timeZone !== current.timeZone : false
  const targetTimezone = variant === 'delete' ? replacement?.timeZone : next?.timeZone
  const timezoneShifts = targetTimezone && current.timeZone ? targetTimezone !== current.timeZone : false
  const bookingLinksLine = `Booking links update to the new location${timezoneShifts && targetTimezone ? ` and ${formatTimezoneLabel(targetTimezone)}` : ''}`

  const affectedIsSingular = affectedCount === 1
  const affectedPossessive = affectedIsSingular ? 'its' : 'their'
  const eventTypesLabel = <span className='bold'>{pluralizeEventTypes(affectedCount)}</span>

  const bannerMessage =
    variant === 'update' ? (
      affectedCount > 0 ? (
        <>
          {eventTypesLabel} will update {affectedPossessive} location after confirming.
        </>
      ) : (
        'No shared event types use this location yet.'
      )
    ) : variant === 'change-default' ? (
      affectedCount > 0 ? (
        <>
          {eventTypesLabel} bound to this default will change {affectedPossessive} location.
        </>
      ) : (
        'No shared event types are bound to this default yet.'
      )
    ) : affectedCount > 0 ? (
      <>
        Deleting <span className='bold'>{current.name}</span> is permanent. {eventTypesLabel} {affectedIsSingular ? 'uses' : 'use'} it and{' '}
        {affectedIsSingular ? 'needs' : 'need'} a replacement.
      </>
    ) : (
      <>
        Deleting <span className='bold'>{current.name}</span> is permanent.
      </>
    )

  const handleConfirm = () => {
    onConfirm(replacementId)
  }

  return (
    <Modal
      show
      size='lg'
      onHide={onCancel}
      header={<h5 className='mb-0'>{config.title}</h5>}
      headerBodySeparator
      body={
        isUnusedDelete ? (
          <span className='paragraph-medium'>
            This permanently deletes <span className='bold'>{current.name}</span>. Nothing currently uses it — no event types, scheduled meetings, or booking
            links — so it&apos;s safe to remove.
          </span>
        ) : (
          <div className='d-flex flex-column gap-3'>
            <div
              className={classNames('d-flex align-items-center zocks-gap-0.5 zocks-py-0.75 px-3 rounded-3 paragraph-medium', {
                'zocks-bg-color-warning zocks-border-color-warning': !config.danger,
                'zocks-bg-color-danger-background zocks-border-color-danger': config.danger,
              })}
            >
              <i
                className={classNames(
                  'flex-shrink-0',
                  config.danger ? 'bi bi-exclamation-circle-fill zocks-text-danger' : 'bi bi-exclamation-triangle-fill zocks-text-warning-dark',
                )}
              />
              <span>{bannerMessage}</span>
            </div>

            {variant === 'delete' ? (
              replacementOptions && replacementOptions.length > 0 ? (
                <div className='d-flex flex-column zocks-gap-0.5 p-3 rounded-3 zocks-bg-color-gray-100'>
                  <label className='bold paragraph-medium' htmlFor='location-replacement'>
                    Replace with
                  </label>
                  <Form.Select
                    id='location-replacement'
                    value={replacementId ?? ''}
                    onChange={(event) => setReplacementId(event.currentTarget.value || undefined)}
                  >
                    {replacementOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.name}
                      </option>
                    ))}
                  </Form.Select>
                  {replacementTimezoneChanges ? (
                    <span className='d-flex align-items-center zocks-gap-0.5 paragraph-medium zocks-text-gray-600'>
                      <i className='bi bi-exclamation-triangle-fill zocks-text-warning-dark flex-shrink-0' />
                      {replacement?.name} uses a different timezone than {current.name}.
                    </span>
                  ) : null}
                </div>
              ) : null
            ) : (
              <div className='d-flex align-items-stretch gap-3'>
                <LocationCard location={current} struckAgainst={variant === 'update' ? next : undefined} />
                <i className='bi bi-arrow-right align-self-center zocks-text-gray-500' />
                {next ? <LocationCard location={next} /> : null}
              </div>
            )}

            <div className='d-flex flex-column zocks-gap-0.5'>
              <span className='paragraph-medium'>
                Updating affects <span className='bold'>scheduled meetings</span> and <span className='bold'>sent booking links</span>:
              </span>
              <span className='d-flex align-items-center zocks-gap-0.5 paragraph-medium'>
                <i className='bi bi-calendar zocks-text-gray-600' />
                Meetings keep their current location
              </span>
              <span className='d-flex align-items-center zocks-gap-0.5 paragraph-medium'>
                <i className='bi bi-link-45deg zocks-text-gray-600' />
                {bookingLinksLine}
              </span>
            </div>
          </div>
        )
      }
      showFooterActions
      footerPrimaryActionTitle={config.confirmLabel}
      footerSecondaryActionTitle='Cancel'
      footerPrimaryActionButtonVariant={config.danger ? 'danger' : 'primary'}
      footerPrimaryActionLoading={isLoading}
      onFooterPrimaryAction={handleConfirm}
      onFooterSecondaryAction={onCancel}
    />
  )
}
